import { useState, useEffect, useCallback } from 'react';
import { Header, MobileNav } from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { statsAPI } from '../../lib/api';
import { getServiceName } from '../../lib/constants';
import {
  ChartBar, CurrencyEur, Star, CheckCircle, Clock, XCircle,
  TrendUp, CalendarBlank, ArrowLeft
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const PERIODS = [
  { value: '7', label: '7 dias' },
  { value: '30', label: '30 dias' },
  { value: '90', label: '90 dias' },
  { value: 'all', label: 'Todo' },
];

function MiniBar({ value, max, color = 'bg-primary' }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="w-full bg-muted rounded-full h-2">
      <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function StatBox({ title, value, subtitle, icon: Icon, color }) {
  return (
    <Card data-testid={`analytics-stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center flex-shrink-0`}>
            <Icon weight="duotone" className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground">{title}</p>
            {subtitle && <p className="text-xs text-muted-foreground/70">{subtitle}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SimpleChart({ data, height = 160 }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
        Sem dados para este período
      </div>
    );
  }

  const maxRequests = Math.max(...data.map(d => d.requests), 1);

  return (
    <div className="flex items-end gap-1 sm:gap-1.5" style={{ height }} data-testid="analytics-chart">
      {data.map((d, i) => {
        const barH = Math.max(4, (d.requests / maxRequests) * (height - 24));
        const completedH = d.completed > 0 ? Math.max(2, (d.completed / maxRequests) * (height - 24)) : 0;
        const dateLabel = d.date.slice(5);
        return (
          <div key={d.date} className="flex flex-col items-center flex-1 min-w-0 group relative">
            <div className="absolute -top-8 hidden group-hover:block bg-foreground text-background text-xs px-2 py-1 rounded whitespace-nowrap z-10">
              {d.date}: {d.requests} pedidos, {d.earnings}&euro;
            </div>
            <div className="w-full flex flex-col items-center gap-0.5">
              <div className="w-full max-w-6 bg-primary/20 rounded-t" style={{ height: barH }}>
                <div className="w-full bg-primary rounded-t" style={{ height: completedH }} />
              </div>
            </div>
            {(i === 0 || i === data.length - 1 || i === Math.floor(data.length / 2)) && (
              <span className="text-[9px] text-muted-foreground mt-1 truncate w-full text-center">{dateLabel}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ProviderAnalyticsPage() {
  const [period, setPeriod] = useState('30');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const data = await statsAPI.getProviderAnalytics(period);
      setAnalytics(data);
    } catch (error) {
      toast.error('Erro ao carregar analytics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const s = analytics?.summary || {};

  return (
    <div className="min-h-screen bg-background" data-testid="provider-analytics-page">
      <Header />
      <main className="container mx-auto px-4 py-6 pb-24 sm:pb-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <Link to="/provider/dashboard">
              <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold font-outfit">Analytics</h1>
              <p className="text-sm text-muted-foreground">Desempenho do seu negócio</p>
            </div>
          </div>

          {/* Period selector */}
          <div className="flex gap-2 mb-6" data-testid="period-selector">
            {PERIODS.map(p => (
              <Button
                key={p.value}
                variant={period === p.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriod(p.value)}
                className="rounded-full"
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[1,2,3,4].map(i => <Card key={i} className="h-24 animate-pulse bg-muted" />)}
            </div>
          ) : (
            <>
              {/* Summary stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <StatBox title="Pedidos" value={s.total_requests || 0} icon={CalendarBlank} color="bg-blue-500" />
                <StatBox title="Concluídos" value={s.completed || 0} icon={CheckCircle} color="bg-green-500" />
                <StatBox title="Receita" value={`${s.earnings || 0}\u20AC`} icon={CurrencyEur} color="bg-amber-500" />
                <StatBox title="Taxa Aceitação" value={`${s.acceptance_rate || 0}%`} icon={TrendUp} color="bg-purple-500" />
              </div>

              {/* Secondary stats */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <StatBox title="Pendentes" value={s.pending || 0} icon={Clock} color="bg-orange-500" />
                <StatBox title="Avaliação" value={s.avg_rating || 0} subtitle={`${s.review_count || 0} avaliações`} icon={Star} color="bg-amber-600" />
                <StatBox title="Recusados" value={s.rejected || 0} icon={XCircle} color="bg-red-400" />
              </div>

              {/* Chart */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ChartBar className="w-5 h-5" />
                    Pedidos por Dia
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SimpleChart data={analytics?.chart_data || []} />
                  <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-primary/20" /> Total</div>
                    <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-primary" /> Concluídos</div>
                  </div>
                </CardContent>
              </Card>

              {/* Service breakdown */}
              {(analytics?.service_breakdown || []).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Por Tipo de Serviço</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4" data-testid="service-breakdown">
                    {analytics.service_breakdown.map(sb => (
                      <div key={sb.type}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-medium">{getServiceName(sb.type)}</span>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span>{sb.count} pedidos</span>
                            <Badge variant="outline">{sb.earnings}&euro;</Badge>
                          </div>
                        </div>
                        <MiniBar value={sb.completed} max={sb.count} color="bg-green-500" />
                        <p className="text-xs text-muted-foreground mt-1">{sb.completed}/{sb.count} concluídos</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </motion.div>
      </main>
      <MobileNav />
    </div>
  );
}
